import React, { useState, useEffect, useMemo } from "react";
import "./SingleStock.css";
import useLocalSearch from "../../hooks/useLocalSearch";
import { useNavigate, useParams } from "react-router-dom";
import { useSelectedStock } from "../../context/FavStockContext";
import { useAuth } from "../../context/AuthNewContext";
import { useMarket } from "../../context/MarketDhanContext";
import { MdDelete } from "react-icons/md";
import { useAIChat } from "../../context/AIChatContext";
import { useStock } from "../../context/StockContext";
import { useSearchStock } from "../../context/SearchStockContext";
import { useSecurityMaster } from "../../context/SecurityMasterContext";
import { useOption } from "../../context/OptionContext";
import { useMarketIndicators } from "../../context/MarketIndicatorContext";
import { useWatchlist } from "../../context/WatchlistContext";
import { useIndicators } from "../../context/IndicatorContext";
// import { useStockName } from "../../context/StockNameContext";
import { useMoreStockContext } from "../../context/MoreStockContext";
import { useMarketToggle } from "../../context/MarketContext";
import { useMarketSnapshot } from "../../context/MarketSnapshotContext";
import { useTechnicalSnapshot } from "../../context/TechnicalSnapshotContext";
import { useIndexSnapshot } from "../../context/IndexContext";
import { TOOLTIPS } from "../../helper/tooltilData";
import Gauge from "../Gauge/Gauge";
import { FaSpinner } from "react-icons/fa6";
import { FiRefreshCw } from "react-icons/fi";
import { useAdmin } from "../../context/AdminContext";
import { useVariant } from "../../hooks/useVariant";
import { toast } from "react-toastify";

import { FiStar } from "react-icons/fi"; // outline
import { useStockSnapshot } from "../../context/StockSnapshotContext";

import { useChartIndicator } from "../../context/ChartIndicatorContext";
import { FaStar, FaRegStar } from "react-icons/fa"; // filled
import { usePdf } from "../../context/PdfContext";
import { getTechnicalScores } from "../../helper/scoreHelper";
import OverallScoreGauge from "../OverallGauge/OverallGauge";

import UpgradeModal from "../UpgradeModal/UpgradeModal";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  Legend,
  ComposedChart,
} from "recharts";
import CandleChart from "../CandleChart/CandleChart";
import StockHelper from "../StockHelper/StockHelper";

const ENTRY_ZONE_PCT = 0.002; // 0.2% above VWAP = entry zone ceiling
const STOP_BUFFER = 0.001; // 0.1% below VWAP = stop loss buffer
const TARGET_PCT = 0.005; // 0.5% above entry = short-term target
const TARGET_PCT_PY = 0.01; // 1.0% above entry = python-style target

const runTradingAnalysis = ({
  ltp,
  vwap,
  prevClose,
  prevVWAP,
  volume,
  avgVolume,
  r1,
  s1,
  rLabel = "R1",
  sLabel = "S1",
  position,
}) => {
  if (!ltp || !vwap) return {};

  const isAbove = ltp > vwap;
  const isBelow = ltp < vwap;
  const diff = ltp - vwap;
  const inPosition = !!position?.inPosition;
  const entryPrice = position?.entryPrice ?? null;

  // ── DERIVED VALUES (always computed) ──
  const stopLoss = isAbove
    ? (vwap * (1 - STOP_BUFFER)).toFixed(2)
    : (vwap * (1 + STOP_BUFFER)).toFixed(2);

  const targets = {
    primary: {
      label: isAbove ? rLabel || "R1" : sLabel || "S1",
      value: isAbove ? (r1 ?? null) : (s1 ?? null),
    },
  };

  // ── NOT IN POSITION → ENTRY LOGIC ──
  if (!inPosition) {
    const { entry, signal, newPosition, pythonSignal, pythonTrend } =
      resolveEntry(ltp, vwap, prevClose, prevVWAP, volume, avgVolume, position);

    const traderNote = pythonSignal || `WAIT → VWAP ₹${vwap.toFixed(2)}`;

    return {
      entry,
      signal,
      exit: null, // no exit when not in a trade
      targets,
      stopLoss,
      traderNote,
      newPosition,
      pythonSignal,
      pythonTrend,
    };
  }

  // ── IN POSITION → EXIT LOGIC ──
  const { exit, newPosition, pythonSignal, pythonTrend } = resolveExit(
    ltp,
    vwap,
    entryPrice,
    position,
  );

  const traderNote = `IN TRADE → Entry ₹${entryPrice} | Target ₹${(
    entryPrice *
    (1 + TARGET_PCT_PY)
  ).toFixed(2)}`;

  return {
    entry: {
      label: "In Trade",
      msg: `Entry ₹${entryPrice} | LTP ₹${ltp}`,
      type: "active",
    },
    signal: "IN TRADE",
    exit,
    targets,
    stopLoss,
    traderNote,
    newPosition,
    pythonSignal,
    pythonTrend,
  };
};

function resolveEntry(
  ltp,
  vwap,
  prevClose,
  prevVWAP,
  volume,
  avgVolume,
  position,
) {
  const ceiling = vwap * (1 + ENTRY_ZONE_PCT);
  const isInZone = ltp >= vwap && ltp <= ceiling;
  const isReclaim = prevClose < (prevVWAP || vwap) && ltp > vwap;
  const hasVolume = !volume || !avgVolume || volume > avgVolume * 0.8;

  const makeEntry = (label, msg, type) => ({ label, msg, type });
  const openPosition = () => ({
    inPosition: true,
    entryPrice: Number(ltp.toFixed(2)),
  });

  // Priority 1: VWAP Reclaim (strongest)
  if (isReclaim && hasVolume) {
    return {
      entry: makeEntry("VWAP Reclaim Buy", "Strong breakout above VWAP", "buy"),
      signal: `Bullish (Reclaim) @ ₹${ltp}`,
      newPosition: openPosition(),
      pythonSignal: `BUY at ₹${ltp}, VWAP ₹${vwap.toFixed(2)}`,
      pythonTrend: "BULLISH ENTRY",
    };
  }

  // Priority 2: Pullback Buy (in entry zone)
  if (isInZone) {
    return {
      entry: makeEntry("Pullback Buy", "VWAP Support Zone", "buy"),
      signal: `Bullish (Pullback) @ ₹${ltp}`,
      newPosition: openPosition(),
      pythonSignal: `BUY at ₹${ltp}, VWAP ₹${vwap.toFixed(2)}`,
      pythonTrend: "BULLISH ENTRY",
    };
  }

  // Priority 3: Above VWAP (fallback bullish)
  if (ltp > vwap) {
    return {
      entry: makeEntry("Fallback Bullish", "Above VWAP (fallback)", "buy"),
      signal: `Bullish (Fallback) @ ₹${ltp}`,
      newPosition: position,
      pythonSignal: `LTP ₹${ltp} is above VWAP`,
      pythonTrend: "TRENDING BULLISH",
    };
  }

  // Priority 4: Below VWAP (fallback bearish)
  if (ltp < vwap) {
    return {
      entry: makeEntry("Fallback Bearish", "Below VWAP (fallback)", "sell"),
      signal: `Bearish (Fallback) @ ₹${ltp}`,
      newPosition: position,
      pythonSignal: `LTP ₹${ltp} is below VWAP`,
      pythonTrend: "TRENDING BEARISH",
    };
  }

  // Priority 5: Exactly at VWAP (rare edge case)
  return {
    entry: makeEntry("Neutral", "Consolidating at VWAP", "neutral"),
    signal: `Neutral @ ₹${ltp}`,
    newPosition: position,
    pythonSignal: `LTP ₹${ltp} at VWAP ₹${vwap.toFixed(2)}`,
    pythonTrend: "NEUTRAL",
  };
}

function resolveExit(ltp, vwap, entryPrice, position) {
  const target = entryPrice * (1 + TARGET_PCT);
  const stopLoss = vwap * (1 - STOP_BUFFER);
  const closePosition = () => ({ inPosition: false, entryPrice: null });

  // Priority 1: Target hit
  if (ltp >= target) {
    return {
      exit: `Target Hit 🚀 SELL @ ₹${ltp}`,
      newPosition: closePosition(),
      pythonSignal: `SELL at ₹${ltp}, Gain: ${(((ltp - entryPrice) / entryPrice) * 100).toFixed(2)}%`,
      pythonTrend: "EXIT PROFIT",
    };
  }

  // Priority 2: VWAP breakdown
  if (ltp < stopLoss) {
    return {
      exit: `VWAP Breakdown ❌ SELL @ ₹${ltp}`,
      newPosition: closePosition(),
      pythonSignal: `LOSS at ₹${ltp}, Below VWAP`,
      pythonTrend: "EXIT STOP",
    };
  }

  // Priority 3: Hold
  return {
    exit: `HOLD → LTP ₹${ltp}`,
    newPosition: position,
    pythonSignal: `HOLD at ₹${ltp}, Entry ₹${entryPrice}`,
    pythonTrend: "HOLDING",
  };
}
const SingleStock = () => {
  //   const [results, setResults] = useState([]);
  const [searchText, setSearchText] = useState("");

  const [securityId, setSecurityId] = useState("");
  const [timeFrame, setTimeFrame] = useState("min");
  const [chartType, setChartType] = useState("candles");
  const [selectedSymbol, setSelectedSymbol] = useState("");
  const [isRemoved, setIsRemoved] = useState(false);
  const {
    query: localSearch,
    setQuery: setLocalSearch,
    indexResults: localIndexResults,
    equityResults: localEquityResults,
    loading: localSearchLoading,
    clearResults: clearLocalResults,
  } = useLocalSearch();
  const {
    loadDashboardIndexMarket,
    dashboardMarketData,
    resetDashboardMarketData,
    dashboardLoading,
    dashboardError,
    dashboardIndexData,
  } = useMarketSnapshot();
  const [selectedIndex, setSelectedIndex] = useState("");
  const { indexTabs, indexTabError, indexTabLoading, getIndexTabs } =
    useIndexSnapshot();

  const [lastUTSignal, setLastUTSignal] = useState(null);
  const [liveReady, setLiveReady] = useState(false);
  const [resistTimeFrame, setResistTimeFrame] = useState("day");
  const [interval, setInterval] = useState("1d");
  // const [showSearch, setShowSearch] = useState(true);
  const [search, setSearch] = useState("");

  const { user } = useAuth();

  const {
    stockDetail,
    stockDetailLoading,
    stockDetailError,
    getStockDetailSnapshot,
    resetStockDetail,
    getStockFundamentals,
    fundamentals,
  } = useStockSnapshot();
  const [visibleCount, setVisibleCount] = useState(8);
  const [crossovers, setCrossovers] = useState([]);

  const { fetchSearchQuery } = useIndicators();
  // const {
  //   getTechnicalSnapshot,
  //   technicalSnapshot,
  //   initializeTechnicalSnapshot,
  //   resetTechnicalSnapshot,
  // } = useTechnicalSnapshot();

  const navigate = useNavigate();

  const {
    addSelectedStock,
    removeSelectedStock,
    selectedStock,
    clearSelectedStock,
    getSelectedStock,
  } = useSelectedStock();
  const [position, setPosition] = useState({
    inPosition: false,
    entryPrice: null,
  });

  const {
    targetPrice,
    getTargetPrice,
    resetTargetPrice,
    getNameBySymbol,
    stockName,
  } = useMarketIndicators();
  const {
    addToWatchlist,
    removeFromWatchlist,
    resetWatchlist,
    fetchWatchlist,
    watchlist,
    isInWatchlist,
  } = useWatchlist();

  const { fetchBySymbol, pcr } = useOption();
  const { chatOpen } = useAIChat();
  const { downloadStockPDF } = usePdf();
  const [maxChart, setMaxChart] = useState(null);

  const [smartMode, setSmartMode] = useState("intraday");

  const variant = useVariant();

  const [resData, setResData] = useState("daily");

  const {
    getSecurityIdBySymbol,
    securityIdData,
    securityError,
    securityLoading,
  } = useMarket();
  console.log("secureiyIddata", securityIdData);
  // const security_id = securityIdData?.security_id;

  const marketSnapshot = stockDetail?.marketSnapshot;
  const technicalSnapshot = stockDetail?.technicalSnapshot;

  const userID = user?.userID;
  const { market } = useMarketToggle();
  const quote = marketSnapshot?.quote?.data;
  const last_price = quote?.last_price;
  //useEffect to get selected stock in db as fav_stock
  useEffect(() => {
    if (!userID) return;
    getSelectedStock(userID, "STOCK");
  }, [userID]);

  console.log("afv stock for userid", selectedStock);
  const dbSymbol = selectedStock?.[0]?.symbol;
  const dbSecurityId = Number(selectedStock?.[0]?.securityId) || 0;
  const finalSymbol = selectedSymbol || dbSymbol;
  const finalSecurityId = Number(securityId) || dbSecurityId;
  const ltp = quote?.last_price;

  console.log("Selected stock in db", selectedStock);
  const selectedLength = selectedStock?.length;
  console.log("selectedStock", selectedLength);
  const handleIndexClick = async (indexName) => {
    setSelectedIndex(indexName);

    await loadDashboardIndexMarket({
      indexName,
    });
  };

  const indexSymbols = Object.values(dashboardMarketData || {})
    .map((item) => {
      const quote = item?.quote?.data;

      return {
        symbol: item?.symbol,
        securityId: item?.security_id,
        lastPrice: quote?.last_price,
        volume: quote?.volume,
        open: quote?.ohlc?.open,
        high: quote?.ohlc?.high,
        low: quote?.ohlc?.low,
        prevClose: quote?.ohlc?.close,
      };
    })
    .filter((item) => item?.symbol && item?.securityId);

  useEffect(() => {
    if (!dbSymbol) return;

    const loadInitialStock = async () => {
      try {
        await getStockDetailSnapshot(dbSymbol);

        await Promise.all([
          getTargetPrice(dbSymbol, market),
          getNameBySymbol(dbSymbol),
          fetchBySymbol(dbSymbol),
        ]);
      } catch (err) {
        console.error("Initial load failed", err);
      }
    };

    loadInitialStock();
  }, [dbSymbol]);
  const localSecurityId = Number(securityId);

  // const finalSecurityId = dbSecurityId || localSecurityId;
  console.log("DB SECURITY ID:", dbSecurityId);

  console.log("LOCAL SECURITY ID:", localSecurityId);

  console.log("FINAL SECURITY ID:", finalSecurityId);

  console.log("TYPE:", typeof finalSecurityId);

  const symbol = selectedStock?.symbol;
  const hasSelectedStock = !!selectedStock?.[0]?.symbol;

  const handleAdd = async ({ symbol, securityId }) => {
    try {
      setIsRemoved(false);
      setLocalSearch("");
      clearLocalResults();
      if (!symbol || !securityId) return;

      const formattedSymbol = symbol.trim().toUpperCase();
      const numericSecurityId = Number(securityId);

      // ✅ set local state immediately — UI shows instantly
      setSelectedSymbol(formattedSymbol);
      setSecurityId(numericSecurityId);
      setLastUTSignal(null);
      setPosition({ inPosition: false, entryPrice: null });

      // ✅ save to DB (don't clear first — just overwrite)
      await addSelectedStock({
        userID,
        symbol: formattedSymbol,
        securityId: numericSecurityId,
      });
      toast.success(`${formattedSymbol} added successfully.`);

      // ✅ load all data in parallel
      // const [marketData, technicalData] = await Promise.all([
      //   getMarketSnapshot(formattedSymbol),
      //   getTechnicalSnapshot(formattedSymbol),
      // ]);

      // // ✅ initialize only if not found in DB
      // if (!marketData) {
      //   await initializeMarketSnapshot({
      //     symbol: formattedSymbol,
      //     securityId: numericSecurityId,
      //   });
      // }

      // if (!technicalData) {
      //   await initializeTechnicalSnapshot({
      //     symbol: formattedSymbol,
      //     securityId: numericSecurityId,
      //   });
      // }

      // await Promise.all([
      //   getTargetPrice(formattedSymbol, market),
      //   getNameBySymbol(formattedSymbol),
      //   fetchBySymbol(formattedSymbol),
      // ]);
      await Promise.all([
        getStockDetailSnapshot(formattedSymbol),
        getTargetPrice(formattedSymbol, market),
        getNameBySymbol(formattedSymbol),
        fetchBySymbol(formattedSymbol),
      ]);

      // ✅ refetch selectedStock LAST — after everything is loaded
      await getSelectedStock(userID);
    } catch (err) {
      console.error("handleAdd failed:", err);
    }
  };

  const handleRemove = async () => {
    try {
      setIsRemoved(true);

      await removeSelectedStock({ userID, symbol: finalSymbol, type: "STOCK" });

      clearSelectedStock();
      // resetMarketSnapshot();
      // resetTechnicalSnapshot();
      resetStockDetail();
      resetTargetPrice();

      setSecurityId("");
      setSelectedSymbol("");
      setLocalSearch("");
      clearLocalResults();
      toast.success(`${finalSymbol} removed successfully.`);
    } catch (err) {
      console.error("REMOVE FAILED:", err);
    }
  };

  const supportedIndices = [
    {
      displayName: "NIFTY 50",
      securityId: "13",
    },
    {
      displayName: "NIFTY BANK",
      securityId: "25",
    },
    {
      displayName: "NIFTY IT",
      securityId: "29",
    },
    {
      displayName: "NIFTY MIDCAP 100",
      securityId: "37",
    },
    {
      displayName: "NIFTY MIDCAP 50",
      securityId: "20",
    },
    {
      displayName: "NIFTY NEXT 50",
      securityId: "38",
    },
  ];

  console.log("Dahsboard index data ", dashboardIndexData);
  return (
    <>
      {!hasSelectedStock && (
        <div>
          {/* FUture scope  */}
          {/* <div
            className="search-option-input"
            style={{
              borderRadius: "12px",
              width: "50%",
              display: "flex",
              alignItems: "center",
              padding: "0px 10px",
              background: "white",
              margin: "10px auto",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.4)",
              position: "relative",
            }}
          >
            <input
              type="text"
              placeholder="Search Stock"
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              className="search-option-chain"
              style={{
                width: "100%",
                padding: "10px",
                outline: "none",
                border: "none",
              }}
            />

            {(localIndexResults?.length > 0 ||
              localEquityResults?.length > 0) && (
              <div className="search-optionpage-dropdown index-dropwon">
                {localIndexResults.length > 0 && (
                  <>
                    <div className="search-section-title">Indices</div>

                    {localIndexResults.map((item, index) => (
                      <div
                        key={`index-${index}`}
                        className="search-option-dropdown-item"
                        onClick={async () => {
                          await handleAdd({
                            symbol: item?.underlyingSymbol,
                            securityId: item?.securityId,
                          });
                          clearLocalResults();
                        }}
                      >
                        <div className="dropdown-symbol">
                          {item?.underlyingSymbol}
                        </div>
                        <div className="dropdown-company">
                          {item?.displayName}
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {localEquityResults?.length > 0 && (
                  <>
                    <div className="search-section-title">Stocks</div>

                    {localEquityResults.map((item, index) => (
                      <div
                        key={`equity-${index}`}
                        className="search-option-dropdown-item"
                        onClick={async () => {
                          await handleAdd({
                            symbol: item?.underlyingSymbol,
                            securityId: item?.securityId,
                            type: "STOCK",
                          });
                          clearLocalResults();
                        }}
                      >
                        <div className="dropdown-symbol">
                          {item?.underlyingSymbol}
                        </div>
                        <div className="dropdown-company">
                          {item?.displayName}
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}

            {localSearchLoading && <p>Searching...</p>}
          </div> */}
          <div className="one-index-note">
            <h3>What is One Stock?</h3>

            <p>
              One Stock allows you to analyze an individual stock with live
              price, technical indicators, market insights, support & resistance
              levels, return performance, charts and option analytics.
            </p>

            <p>
              Currently, stocks can be selected only from the supported indices:
            </p>

            {/* <div className="one-index-tags">
              <span>NIFTY 50</span>
              <span>NIFTY NEXT 50</span>
              <span>NIFTY IT</span>

              <span>NIFTY BANK</span>
              <span>NIFTY MIDCAP 50</span>
              <span>NIFTY MIDCAP 100</span>
            </div> */}
            <div className="supported-index-grid">
              {supportedIndices.map((item) => (
                <button
                  key={item.displayName}
                  className={`supported-index-card ${
                    selectedIndex === item.displayName ? "active" : ""
                  }`}
                  onClick={() => handleIndexClick(item.displayName)}
                >
                  {item.displayName}
                </button>
              ))}
            </div>

            <small>
              More indices and stocks will be added in future releases.
            </small>
          </div>
        </div>
      )}

      <>
        {hasSelectedStock && (
          <div>
            <StockHelper
              symbol={finalSymbol}
              securityId={finalSecurityId}
              type="STOCK"
              onDelete={handleRemove}
            />
          </div>
        )}

        {!hasSelectedStock && (
          <div>
            {dashboardLoading && <p>Loading stocks...</p>}

            {!dashboardLoading && selectedIndex && indexSymbols.length > 0 && (
              <div className="index-symbol-section">
                <h3 className="index-symbol-section-head">
                  List of Stocks in {selectedIndex}. You can add anyone of them
                  by clicking on it.
                </h3>

                <div className="index-symbol-grid">
                  {indexSymbols.map((stock) => (
                    <button
                      key={stock.symbol}
                      className="index-symbol-card"
                      onClick={() =>
                        handleAdd({
                          symbol: stock.symbol,
                          securityId: stock.securityId,
                        })
                      }
                    >
                      <span>{stock.symbol}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </>
    </>
  );
};

export default SingleStock;
